import React, { useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Sparkle } from './Sparkle';
import { BRAND } from '../theme/tokens';
import { BTN_CHOOSE_AI } from '../theme/poButtonTokens';

/**
 * Canonical "AI Import" drop banner (v1.27).
 * Round purple sparkle circle + indigo Choose file (production parity).
 */
export interface AiImportBannerProps {
  onFile: (file: File) => void;
  parsing?: boolean;
  title?: string;
  subtitle?: string;
  parsingLabel?: string;
  accept?: string;
  buttonLabel?: string;
  dense?: boolean;
  /** inline = compact horizontal bar (default); hero = tall centred drop target */
  variant?: 'inline' | 'hero';
  /** Stretch to full container width (reconcile import strip) */
  fullWidth?: boolean;
}

export const AiImportBanner: React.FC<AiImportBannerProps> = ({
  onFile,
  parsing = false,
  title = 'AI Import',
  subtitle = 'Drag files here · AI Imports line items to the document and attaches the file',
  parsingLabel = 'Reading document…',
  accept = 'application/pdf,image/*,.csv,.txt',
  buttonLabel = 'Choose file',
  dense = false,
  variant = 'inline',
  fullWidth = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const isHero = variant === 'hero';

  const pickFile = () => { if (!parsing) inputRef.current?.click(); };
  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    if (e.target) e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (parsing) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <Box
      className="no-print"
      onDragOver={e => { e.preventDefault(); if (!parsing) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={(isHero || fullWidth) && !parsing ? pickFile : undefined}
      sx={{
        width: fullWidth ? '100%' : undefined,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isHero ? 'column' : 'row',
        alignItems: isHero ? 'center' : 'center',
        textAlign: isHero ? 'center' : 'left',
        gap: isHero ? 1.25 : 1.75,
        px: isHero ? 2.5 : 2,
        py: isHero ? 3 : (dense ? 1 : fullWidth ? 1.5 : 1.25),
        minHeight: isHero ? 152 : fullWidth ? 72 : undefined,
        borderRadius: isHero ? 2 : 2.5,
        border: `1.5px dashed ${dragging ? BRAND : isHero ? '#b39ddb' : fullWidth ? '#b39ddb' : '#c9b3e8'}`,
        backgroundColor: dragging ? '#efe6fb' : isHero || fullWidth ? '#faf8ff' : '#f7f2fd',
        cursor: (isHero || fullWidth) && !parsing ? 'pointer' : 'default',
        transition: 'all .15s',
        '&:hover': (isHero || fullWidth) && !parsing ? { backgroundColor: '#f3edfc', borderColor: BRAND } : {},
      }}
    >
      <input ref={inputRef} type="file" accept={accept} hidden onChange={onInput} />

      <Box sx={{
        width: isHero ? 48 : 30,
        height: isHero ? 48 : 30,
        borderRadius: '50%',
        backgroundColor: BRAND,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {parsing
          ? <CircularProgress size={isHero ? 20 : 14} sx={{ color: '#fff' }} />
          : <Sparkle size={isHero ? 22 : 14} color="#fff" />}
      </Box>

      <Box sx={{ flex: isHero ? '0 1 auto' : 1, minWidth: 0, maxWidth: isHero ? 420 : undefined }}>
        <Typography sx={{
          fontSize: isHero ? '1rem' : '0.85rem',
          fontWeight: 800,
          color: BRAND,
          lineHeight: 1.25,
        }}>
          {title}
        </Typography>
        <Typography sx={{
          fontSize: isHero ? '0.78rem' : '0.72rem',
          color: '#8a7aa6',
          fontStyle: 'italic',
          mt: 0.35,
          lineHeight: 1.45,
        }}>
          {parsing ? parsingLabel : subtitle}
        </Typography>
      </Box>

      <Button
        variant="contained"
        disableElevation
        onClick={e => { e.stopPropagation(); pickFile(); }}
        disabled={parsing}
        sx={{
          ...BTN_CHOOSE_AI,
          ...(isHero ? { mt: 0.5, px: 2.5, py: 0.85, fontSize: '0.82rem' } : {}),
        }}
      >
        <Sparkle size={12} color="#fff" />
        {buttonLabel}
      </Button>
    </Box>
  );
};

export default AiImportBanner;
